// EJE FRONTEND (ARQUITECTURA_FRONT_PROPUESTA §7): hooks TanStack Query del caso de uso "tarjetas
// PCF" del dominio retail. Componen el service-client `api/services/retail-cards/`.
//
// El inventario se lleva por CANTIDAD: saldo + libro mayor de movimientos. El libro se pagina con
// el cursor opaco que devuelve el propio endpoint.
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type InfiniteData,
} from '@tanstack/react-query';
import {
    getCardStock,
    listCardBatches,
    registerCardBatch,
    writeOffCardStock,
    getStockAlerts,
    type CardBatchPage,
} from '@/api/services/retail-cards/retail-cards.client';
import { ApiError } from '@/api/errors';
import type { CardStockResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockResponse';
import type { CardStockBalanceResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockBalanceResponse';
import type { CardStockMovementResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockMovementResponse';
import type { CardBatchResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardBatchResponse';
import type { StockAlertsResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/StockAlertsResponse';
import type { RegisterCardBatchRequest } from '@fiado/type-kit/bin/retailCards/dtos/requests/RegisterCardBatchRequest';
import type { WriteOffCardStockRequest } from '@fiado/type-kit/bin/retailCards/dtos/requests/WriteOffCardStockRequest';

/** Cuántos asientos del libro mayor trae cada página. */
const MOVEMENTS_PAGE_SIZE = 25;

const KEYS = {
    stock: (retailerId: string, storeId: string) =>
        ['retail', 'cardStock', retailerId, storeId] as const,
    batches: (retailerId: string, storeId: string) =>
        ['retail', 'cardBatches', retailerId, storeId] as const,
    alerts: (retailerId: string) => ['retail', 'stockAlerts', retailerId] as const,
};

/**
 * La tienda no tiene ítem de saldo porque nunca recibió un lote. Es inventario en cero, no una
 * falla: la pantalla lo pinta como vacío y NO como error.
 */
export function isStockNotFound(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'CARD_STOCK_NOT_FOUND';
}

/**
 * GET …/card-stock — saldo + libro mayor paginado.
 *
 * El saldo viaja en TODAS las páginas; se toma el de la primera, que es la lectura del momento en
 * que se abrió la pantalla. Tomarlo de la última haría que traer historia viejo cambiara el número
 * de arriba sin que nadie moviera inventario.
 */
export function useCardStock(retailerId: string, storeId: string) {
    return useInfiniteQuery<
        CardStockResponse,
        Error,
        InfiniteData<CardStockResponse>,
        readonly unknown[],
        string | undefined
    >({
        queryKey: KEYS.stock(retailerId, storeId),
        queryFn: ({ pageParam }) =>
            getCardStock(retailerId, storeId, {
                limit: MOVEMENTS_PAGE_SIZE,
                startKey: pageParam,
            }),
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextToken,
        enabled: !!retailerId && !!storeId,
        // Sin saldo no hay nada que reintentar: el 404 es la respuesta correcta y reintentarlo solo
        // deja la pantalla en carga tres veces más.
        retry: (count, error) => !isStockNotFound(error) && count < 2,
    });
}

/** GET …/card-batches — los lotes que recibió la tienda, más recientes primero. */
export function useCardBatches(retailerId: string, storeId: string) {
    return useQuery<CardBatchPage>({
        queryKey: KEYS.batches(retailerId, storeId),
        queryFn: () => listCardBatches(retailerId, storeId),
        enabled: !!retailerId && !!storeId,
    });
}

/** POST /retailers/{id}/card-batches — registra la entrada de un lote (el storeId va en el body). */
export function useRegisterCardBatch(retailerId: string, storeId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: RegisterCardBatchRequest) =>
            registerCardBatch(retailerId, body, crypto.randomUUID()),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: KEYS.stock(retailerId, storeId) });
            void qc.invalidateQueries({ queryKey: KEYS.batches(retailerId, storeId) });
            void qc.invalidateQueries({ queryKey: KEYS.alerts(retailerId) });
        },
    });
}

/**
 * POST …/card-stock/write-off — da de baja unidades del inventario.
 *
 * El `operationId` viene DENTRO del body y lo fija quien llama: es la clave de idempotencia de la
 * baja y tiene que sobrevivir a los reintentos del mismo intento. Generarlo acá lo haría distinto
 * en cada llamada y un doble clic descontaría el doble.
 */
export function useWriteOffCardStock(retailerId: string, storeId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: WriteOffCardStockRequest) =>
            writeOffCardStock(retailerId, storeId, body, body.operationId),
        // `onSettled` y no `onSuccess`: un 409 también deja el saldo de pantalla desactualizado —
        // el de clave reusada porque la baja anterior SÍ se aplicó, y el de saldo insuficiente
        // porque otra caja acaba de mover el inventario.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEYS.stock(retailerId, storeId) });
            void qc.invalidateQueries({ queryKey: KEYS.alerts(retailerId) });
        },
    });
}

/** GET /retailers/{id}/stock-alerts — tiendas bajo el umbral de stock. */
export function useStockAlerts(retailerId: string) {
    return useQuery({
        queryKey: KEYS.alerts(retailerId),
        queryFn: () => getStockAlerts(retailerId),
        enabled: !!retailerId,
    });
}

export type {
    CardStockResponse,
    CardStockBalanceResponse,
    CardStockMovementResponse,
    CardBatchResponse,
    StockAlertsResponse,
};
